import {skipToken} from '@reduxjs/toolkit/query';
import {startOfToday} from 'date-fns';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';
import {isNilOrEmpty, isNotNil, isTrue, toNumber} from 'ramda-adjunct';

import {useGetTenantQuery, useGetVatRatesForCountryQuery} from '@dms/api/core';
import {
  RentalVehicleResponseBody,
  SaleVehicleResponseBody,
  useGetRentalPriceGroupQuery,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {getApiDateString} from 'shared/web';

export const useGetVehicleHeaderParameters = (
  saleVehicle?: SaleVehicleResponseBody,
  rentalVehicle?: RentalVehicleResponseBody
) => {
  const isSaleVehicle = isNotNil(saleVehicle);
  const isRentalVehicle = isNotNil(rentalVehicle);
  const formatPrice = useFormatCurrency();

  const {data: tenant} = useGetTenantQuery();
  const tenantCurrency = tenant?.currency;
  const {data: vatRates} = useGetVatRatesForCountryQuery(
    {country: tenant?.country ?? '', date: getApiDateString(startOfToday())},
    {skip: isNilOrEmpty(tenant)}
  );

  // A rental vehicle need not have a price group. Skipping on the vehicle instead of the id sent
  // `GET /rental-price-group/` with an empty id, whose response carries no `items`
  const rentalPriceGroupId = rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId;
  const {data: rentalVehiclePrices} = useGetRentalPriceGroupQuery(
    isNotNil(rentalPriceGroupId) ? {rentalPriceGroupId} : skipToken
  );

  const primaryParameter = match([isSaleVehicle, isNotNil(saleVehicle?.sellingPrice?.withVat)])
    .with(
      [true, true],
      () =>
        formatPrice(
          Number(saleVehicle!.sellingPrice.withVat!.amount),
          saleVehicle!.sellingPrice.withVat!.currency
        ) ?? EMPTY_PLACEHOLDER
    )
    .with([true, false], always(EMPTY_PLACEHOLDER))
    .otherwise(always(undefined));

  const secondaryParameter = match([isSaleVehicle, isTrue(saleVehicle?.isVatDeductible)])
    .with(
      [true, true],
      always(
        isNotNil(saleVehicle?.sellingPrice?.withoutVat?.amount)
          ? formatPrice(
              Number(saleVehicle!.sellingPrice.withoutVat!.amount),
              saleVehicle!.sellingPrice.withoutVat!.currency
            ) + ` ${i18n.t('general.labels.withoutVat')}`
          : i18n.t('entity.vehicle.labels.VATDeductible')
      )
    )
    .with([true, false], always(i18n.t('entity.vehicle.labels.nondeductible')))
    .otherwise(always(undefined));

  const tertiaryParameter = match([
    isSaleVehicle,
    isNotNil(saleVehicle?.sellingPriceIsForeignCurrency?.priceWithVat) ||
      isNotNil(saleVehicle?.sellingPriceIsForeignCurrency?.priceWithoutVat),
  ])
    .with(
      [true, true],
      () =>
        formatPrice(
          Number(saleVehicle?.sellingPriceIsForeignCurrency?.priceWithVat),
          saleVehicle?.sellingPriceIsForeignCurrency?.foreignCurrencyCode ?? ''
        ) ?? EMPTY_PLACEHOLDER
    )
    .otherwise(always(undefined));

  const usedRentalVehiclePrice = rentalVehiclePrices?.items.find(
    (price) =>
      price.rentalPriceGroupItemId === rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId
  );

  const usedVatRate = vatRates?.rates?.find((rate) => rate.type === rentalVehiclePrices?.vatType);
  const vatRate = toNumber(usedVatRate?.rate);

  const pricePerDay = toNumber(usedRentalVehiclePrice?.price?.rate ?? '0');
  const pricePerKm = toNumber(usedRentalVehiclePrice?.price?.ratePerKilometer ?? '0');
  const pricePerDayWithoutVat = pricePerDay / ((100 + vatRate) / 100);
  const pricePerKmWithoutVat = pricePerKm / ((100 + vatRate) / 100);

  const additionalParameterBlocks = match([isRentalVehicle, isNotNil(usedRentalVehiclePrice)])
    .with([true, true], () => [
      {
        primaryParameter: `${formatPrice(pricePerDay, tenantCurrency ?? '')}/${i18n.t('general.labels.1day')}`,
        secondaryParameter: `${formatPrice(pricePerDayWithoutVat, tenantCurrency ?? '')} ${i18n.t('general.labels.withoutVat')}`,
      },
      {
        primaryParameter: `${formatPrice(pricePerKm, tenantCurrency ?? '')}${i18n.t('entity.rental.labels.ratePerKilometer')}`,
        secondaryParameter: `${formatPrice(pricePerKmWithoutVat, tenantCurrency ?? '')} ${i18n.t('general.labels.withoutVat')}`,
      },
    ])
    .otherwise(always(undefined));

  return {primaryParameter, secondaryParameter, tertiaryParameter, additionalParameterBlocks};
};
